import type { JSX } from 'react';
import { Box, LayoutGrid, BrickWall } from 'lucide-react';
import { useEditor } from '../state/store.js';
import { STAGE_LABEL, STAGE_ORDER, currentStage } from '../state/stages.js';
import type { StageId } from '../state/stages.js';
import type { EditorController } from '../editor/controller.js';
import { plural } from '../copy.js';

const ICONS: Record<StageId, typeof Box> = {
  draw: BrickWall,
  arrange: LayoutGrid,
  view: Box,
};

/**
 * The order of the work, made visible.
 *
 * Every part of this sequence already existed — it was simply undiscoverable:
 * the plan was one tool among seven in a flat rail, and 3D was an unlabelled
 * button beside Export. Nothing said that a building is drawn wall by wall,
 * then arranged, then looked at.
 *
 * Each step carries the state of its own stage rather than a static caption,
 * so the bar doubles as the answer to "what have I actually got so far?" —
 * which wall is open, whether the outline closes, whether 3D is worth opening.
 */
export function StageBar({
  controller,
  run,
}: {
  readonly controller: EditorController | null;
  readonly run: (id: string) => void;
}): JSX.Element {
  const project = useEditor((s) => s.project);
  const tool = useEditor((s) => s.tool);
  const previousTool = useEditor((s) => s.previousTool);
  const view3d = useEditor((s) => s.view3d);
  const setTool = useEditor((s) => s.setTool);
  useEditor((s) => s.docRevision);

  const stage = currentStage({ tool, previousTool, view3d });
  const detail = stageDetail(project, controller);

  const enter = (next: StageId): void => {
    if (next === 'view') {
      run('view.3d');
      return;
    }
    // Returning to Draw keeps the drawing tool you left on, so stepping into
    // the plan and back does not silently rearm a different tool.
    if (next === 'draw') setTool(tool === 'plan' && previousTool !== 'plan' ? previousTool : 'build');
    else setTool('plan');
  };

  return (
    <nav className="stagebar" aria-label="Stages">
      {STAGE_ORDER.map((id, index) => {
        const Icon = ICONS[id];
        const blocked = id === 'view' && project.brickCount === 0;
        return (
          <div className="stagestep" key={id}>
            {index > 0 && <span className="stagearrow" aria-hidden />}
            <button
              className="stagebtn"
              aria-current={stage === id ? 'step' : undefined}
              disabled={blocked}
              onClick={() => enter(id)}
              title={blocked ? `${STAGE_LABEL[id]} · lay some bricks first` : STAGE_LABEL[id]}
            >
              <span className="num" aria-hidden>
                {index + 1}
              </span>
              <Icon size={15} aria-hidden />
              <span className="text">
                <span className="name">{STAGE_LABEL[id]}</span>
                <span className="sub">{blocked ? 'Lay some bricks first' : detail[id]}</span>
              </span>
            </button>
          </div>
        );
      })}
    </nav>
  );
}

/** One line of live state per stage. */
function stageDetail(
  project: ReturnType<typeof useEditor.getState>['project'],
  controller: EditorController | null,
): Record<StageId, string> {
  const wall = project.activeWall;
  const index = project.walls.findIndex((candidate) => candidate.id === project.activeWallId);
  const draw =
    project.walls.length === 1
      ? `${wall.name} · ${plural(wall.model.size)}`
      : `${wall.name} · ${plural(wall.model.size)} · ${index + 1} of ${project.walls.length}`;

  // Assembling is cached against the document revision, so reading it here
  // costs nothing on a render that changed no walls.
  const footprint = controller?.building().footprint ?? null;
  const arrange =
    footprint === null
      ? 'Position the walls'
      : footprint.closed
        ? `Outline closed · ${footprint.ring.length} corners`
        : footprint.openEnds.length === 0
          ? 'More than one loop'
          : `Not closed · ${footprint.openEnds.length} open ${
              footprint.openEnds.length === 1 ? 'end' : 'ends'
            }`;

  return {
    draw,
    arrange,
    view: `${plural(project.brickCount)} in the building`,
  };
}
